import React, { useEffect, useRef, useState } from 'react';
import {
  listAllKbFiles, deleteKbFile,
  listKbForCampaign, listKbForTemplate,
  listCampaignsApi, listTemplates,
  presign, attachKbBatch
} from '../api/api';
import { FaTrash } from 'react-icons/fa';
import axios from 'axios';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fileIconClass(fileName) {
  const ext = (fileName || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return 'kb-file-icon-pdf';
  if (['txt', 'md', 'csv'].includes(ext)) return 'kb-file-icon-txt';
  if (['doc', 'docx'].includes(ext)) return 'kb-file-icon-doc';
  return 'kb-file-icon-default';
}

function fileIconLabel(fileName) {
  return (fileName || '').split('.').pop().toUpperCase().slice(0, 4);
}

// ---------------------------------------------------------------------------
// MultiSelect
// Dropdown with checkboxes. Supports optional grouped options.
//
// options: string[]  OR  { group: string, items: { value, label, disabled?, note? }[] }[]
// selected: string[]
// onChange: (string[]) => void
// chipClass: 'kb-chip-q' | 'kb-chip-s'  (optional, defaults to kb-chip-q)
// ---------------------------------------------------------------------------
const MultiSelect = ({
  options = [],
  selected = [],
  onChange,
  placeholder = 'Choose...',
  chipClass = 'kb-chip-q'
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus the search input when dropdown opens
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 30);
  }, [open]);

  const toggle = (value) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  // Normalise to grouped format
  const rawGroups = options.length > 0 && typeof options[0] === 'object' && 'group' in options[0]
    ? options
    : [{ group: null, items: options.map(o => ({ value: o, label: o })) }];

  // Filter by search query
  const q = search.toLowerCase();
  const groups = rawGroups
    .map(g => ({
      ...g,
      items: g.items.filter(item => {
        const label = typeof item === 'string' ? item : item.label;
        return label.toLowerCase().includes(q);
      })
    }))
    .filter(g => g.items.length > 0);

  const allItems = rawGroups.flatMap(g => g.items);
  const activeSelected = selected.filter(v => !allItems.find(i => i.value === v)?.disabled);

  return (
    <div ref={ref} className="kb-ms-wrap">
      <button
        type="button"
        onClick={() => { setOpen(o => !o); if (open) setSearch(''); }}
        className="kb-ms-trigger"
      >
        <span className={activeSelected.length === 0 ? 'kb-ms-placeholder' : ''}>
          {activeSelected.length === 0 ? placeholder : `${activeSelected.length} selected`}
        </span>
        <svg
          style={{
            width: 12, height: 12, flexShrink: 0, color: '#9ca3af',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s'
          }}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="kb-ms-dropdown" style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Search input */}
          <div className="kb-ms-search-wrap">
            <span className="kb-ms-search-ico">⌕</span>
            <input
              ref={searchRef}
              type="text"
              className="kb-ms-search"
              placeholder={`Search ${placeholder.toLowerCase().replace('select ', '')}...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          </div>

          {/* Options list */}
          <div style={{ maxHeight: 190, overflowY: 'auto' }}>
            {groups.length === 0 ? (
              <p className="kb-ms-empty">No matches for "{search}"</p>
            ) : (
              groups.map((group, gi) => (
                <div key={gi}>
                  {group.group && (
                    <div className="kb-ms-group-hd">{group.group}</div>
                  )}
                  {group.items.map(item => {
                    const value = typeof item === 'string' ? item : item.value;
                    const label = typeof item === 'string' ? item : item.label;
                    const disabled = item.disabled || false;
                    const note = item.note || null;
                    const checked = selected.includes(value);
                    return (
                      <label
                        key={value}
                        className={`kb-ms-opt${disabled ? ' implied' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => !disabled && toggle(value)}
                          style={{ accentColor: '#185FA5' }}
                        />
                        <span style={{ flex: 1 }}>{label}</span>
                        {note && <span className="kb-implied-note">{note}</span>}
                      </label>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer — count + clear all */}
          <div className="kb-ms-footer">
            <span>{activeSelected.length} selected</span>
            {activeSelected.length > 0 && (
              <button
                type="button"
                className="kb-ms-footer-clear"
                onClick={e => { e.stopPropagation(); onChange([]); }}
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      )}

      {activeSelected.length > 0 && (
        <div className="kb-chips">
          {activeSelected.map(v => (
            <span key={v} className={`kb-chip ${chipClass}`}>
              {v}
              <button type="button" className="kb-chip-x" onClick={() => toggle(v)}>✕</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// AttachmentSummary
// Inline badge row on the file list.
// Campaigns: show first 2 + "+N more queues" pill if overflow.
// Scorecards: only shown if no campaigns — show first 3 + overflow.
// ---------------------------------------------------------------------------
const INLINE_LIMIT = 2;

const AttachmentSummary = ({ file, onExpand }) => {
  const { campaigns = [], templates = [], global: isGlobal = false } = file;

  if (isGlobal) {
    return (
      <span className="kb-pill kb-pill-global">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 3 }}>
          <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
        </svg>
        Global
      </span>
    );
  }

  if (!campaigns.length && !templates.length) {
    return (
      <span className="kb-pill kb-pill-none">Unattached</span>
    );
  }

  if (campaigns.length > 0) {
    const visible = campaigns.slice(0, INLINE_LIMIT);
    const overflow = campaigns.length - INLINE_LIMIT;
    return (
      <div className="kb-badges">
        {visible.map(c => (
          <span key={c} className="kb-pill kb-pill-q">{c}</span>
        ))}
        {overflow > 0 && (
          <button
            type="button"
            className="kb-pill kb-pill-more"
            onClick={onExpand}
          >
            +{overflow} more {overflow === 1 ? 'queue' : 'queues'}
          </button>
        )}
      </div>
    );
  }

  // Scorecard-only
  const visible = templates.slice(0, INLINE_LIMIT + 1);
  const overflow = templates.length - (INLINE_LIMIT + 1);
  return (
    <div className="kb-badges">
      {visible.map(t => (
        <span key={t} className="kb-pill kb-pill-s">{t}</span>
      ))}
      {overflow > 0 && (
        <button
          type="button"
          className="kb-pill kb-pill-more"
          onClick={onExpand}
        >
          +{overflow} more
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// AttachmentDetail
// Expanded read-only panel. Shows all campaigns with their implied templates,
// and any scorecard-specific templates separately.
// ---------------------------------------------------------------------------
const AttachmentDetail = ({ file, campaignMap, onClose, onEdit }) => {
  const { campaigns = [], templates = [], global: isGlobal = false } = file;

  if (isGlobal) {
    return (
      <div className="kb-sub-panel">
        <div className="kb-detail-section">
          <p className="kb-detail-label">Global</p>
          <p style={{ fontSize: 12, color: '#6b7280' }}>This file applies to all queues and scorecards.</p>
        </div>
        <div className="kb-detail-footer">
          <button type="button" className="kb-btn" onClick={onClose}>Close</button>
          <button type="button" className="kb-btn kb-btn-primary" onClick={onEdit}>Edit attachments</button>
        </div>
      </div>
    );
  }

  return (
    <div className="kb-sub-panel">
      {campaigns.length > 0 && (
        <div className="kb-detail-section">
          <p className="kb-detail-label">Queues — {campaigns.length} total</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {campaigns.map(c => {
              const impliedTemplates = campaignMap[c] || [];
              return (
                <div key={c} className="kb-camp-chip">
                  <span className="kb-camp-badge">{c}</span>
                  {impliedTemplates.length > 0 && (
                    <span className="kb-camp-tmpl">{impliedTemplates.join(' · ')}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {templates.length > 0 && (
        <div className="kb-detail-section">
          <p className="kb-detail-label">Scorecard-specific</p>
          <div className="kb-badges">
            {templates.map(t => (
              <span key={t} className="kb-pill kb-pill-s">{t}</span>
            ))}
          </div>
        </div>
      )}

      <div className="kb-detail-footer">
        <button type="button" className="kb-btn" onClick={onClose}>Close</button>
        <button type="button" className="kb-btn kb-btn-primary" onClick={onEdit}>Edit attachments</button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// AttachPanel
// Edit panel — pre-populates from existing attachments.
// Scorecard options grouped by parent campaign; templates already implied
// by a selected campaign are greyed out (shown as read-only).
// Saving sends replace: true so the backend fully overwrites the lists.
// ---------------------------------------------------------------------------
const AttachPanel = ({ file, campaigns, templates, campaignMap, templateCampaignMap, onSave, onCancel, saving = false }) => {
  const [isGlobal, setIsGlobal] = useState(file.global || false);
  const [selectedCampaigns, setSelectedCampaigns] = useState(file.campaigns || []);
  const [selectedTemplates, setSelectedTemplates] = useState(file.templates || []);

  const impliedTemplates = new Set(
    selectedCampaigns.flatMap(c => campaignMap[c] || [])
  );

  const scorecardOptions = () => {
    const groups = campaigns
      .filter(c => (campaignMap[c.campaignName] || []).length > 0)
      .map(c => ({
        group: c.campaignName,
        items: (campaignMap[c.campaignName] || []).map(t => ({
          value: t,
          label: t,
          disabled: impliedTemplates.has(t),
          note: impliedTemplates.has(t) ? 'implied' : null
        }))
      }));

    const unassigned = templates
      .map(t => t.templateName)
      .filter(t => !templateCampaignMap[t]?.length);

    if (unassigned.length) {
      groups.push({
        group: 'Unassigned',
        items: unassigned.map(t => ({ value: t, label: t }))
      });
    }

    return groups;
  };

  const handleCampaignChange = (newCampaigns) => {
    setSelectedCampaigns(newCampaigns);
    const newImplied = new Set(newCampaigns.flatMap(c => campaignMap[c] || []));
    setSelectedTemplates(prev => prev.filter(t => !newImplied.has(t)));
  };

  const hasSelection = isGlobal || selectedCampaigns.length > 0 || selectedTemplates.length > 0;

  return (
    <div className="kb-sub-panel">
      <div className="kb-attach-panel">
        {/* Global toggle */}
        <label className={`kb-global-toggle${isGlobal ? ' active' : ''}`}>
          <input
            type="checkbox"
            checked={isGlobal}
            onChange={e => setIsGlobal(e.target.checked)}
          />
          <div className="kb-global-toggle-text">
            <div className="kb-global-toggle-title">Apply to all queues (global)</div>
            <div className="kb-global-toggle-sub">Overrides queue &amp; scorecard selection — available everywhere</div>
          </div>
          {isGlobal && (
            <span className="kb-pill kb-pill-global">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 3 }}>
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
              </svg>
              Global
            </span>
          )}
        </label>

        {/* Queue/scorecard selectors — dim when global, still visible for context */}
        <div className="kb-attach-row" style={isGlobal ? { opacity: 0.35, pointerEvents: 'none' } : undefined}>
          <div className="kb-attach-field">
            <label className="kb-field-lbl">Queues</label>
            <MultiSelect
              options={campaigns.map(c => c.campaignName)}
              selected={selectedCampaigns}
              onChange={handleCampaignChange}
              placeholder="Select queues..."
              chipClass="kb-chip-q"
            />
          </div>

          <div className="kb-attach-field">
            <label className="kb-field-lbl">
              Scorecards{' '}
              <span style={{ fontWeight: 400, color: '#9ca3af' }}>
                {impliedTemplates.size > 0 ? '— implied shown greyed' : '(optional)'}
              </span>
            </label>
            <MultiSelect
              options={scorecardOptions()}
              selected={[...selectedTemplates, ...impliedTemplates]}
              onChange={(vals) => setSelectedTemplates(vals.filter(v => !impliedTemplates.has(v)))}
              placeholder="Select scorecards..."
              chipClass="kb-chip-s"
            />
          </div>
        </div>

        <div className="kb-attach-footer">
          <button type="button" className="kb-btn" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            disabled={!hasSelection || saving}
            onClick={() => onSave({
              s3Key: file.s3Key,
              fileName: file.fileName,
              selectedCampaigns: isGlobal ? [] : selectedCampaigns,
              selectedTemplates: isGlobal ? [] : selectedTemplates,
              global: isGlobal
            })}
            className="kb-btn kb-btn-primary"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// KbManager
// ---------------------------------------------------------------------------
const PAGE_SIZE = 20;

const KbManager = ({ setAlert }) => {
  const [displayFiles, setDisplayFiles] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filterCampaign, setFilterCampaign] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [attachTo, setAttachTo] = useState('campaign');
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [savingFile, setSavingFile] = useState(null); // s3Key of file currently being saved
  const [expandedFile, setExpandedFile] = useState(null);
  const [attachingFile, setAttachingFile] = useState(null);
  const [isUploadGlobal, setIsUploadGlobal] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const searchDebounce = useRef(null);

  const campaignMap = React.useMemo(() => {
    const map = {};
    campaigns.forEach(c => { map[c.campaignName] = c.templates || []; });
    return map;
  }, [campaigns]);

  const templateCampaignMap = React.useMemo(() => {
    const map = {};
    campaigns.forEach(c => {
      (c.templates || []).forEach(t => {
        if (!map[t]) map[t] = [];
        map[t].push(c.campaignName);
      });
    });
    return map;
  }, [campaigns]);

  const loadFiles = async ({ reset = true, nextCursor = null, campaign = filterCampaign, template = filterTemplate, search = searchQuery } = {}) => {
    try {
      const params = {
        limit: PAGE_SIZE,
        ...(nextCursor ? { cursor: nextCursor } : {}),
        ...(search ? { search } : {})
      };

      let res;
      if (campaign)       res = await listKbForCampaign(campaign, params);
      else if (template)  res = await listKbForTemplate(template, params);
      else                res = await listAllKbFiles(params);

      const files = res.files || [];
      setDisplayFiles(prev => reset ? files : [...prev, ...files]);
      setCursor(res.nextCursor || null);
      setHasMore(!!res.hasMore);
    } catch (err) {
      console.error('Failed to load KB files', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load KB files.' });
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const [campRes, tmplRes] = await Promise.all([listCampaignsApi(), listTemplates()]);
        setCampaigns(campRes.campaigns || []);
        setTemplates(tmplRes.templates || []);
        await loadFiles({ reset: true });
      } catch (err) {
        console.error('Failed to load KB data', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load KB files.' });
      } finally {
        setLoading(false);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterCampaign = (value) => {
    setFilterCampaign(value);
    setFilterTemplate('');
    setCursor(null);
    loadFiles({ reset: true, campaign: value, template: '' });
  };

  const handleFilterTemplate = (value) => {
    setFilterTemplate(value);
    setFilterCampaign('');
    setCursor(null);
    loadFiles({ reset: true, campaign: '', template: value });
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    setCursor(null);
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      loadFiles({ reset: true, search: value });
    }, 300);
  };

  const clearFilters = () => {
    setFilterCampaign('');
    setFilterTemplate('');
    setSearchQuery('');
    setCursor(null);
    loadFiles({ reset: true, campaign: '', template: '', search: '' });
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    await loadFiles({ reset: false, nextCursor: cursor });
    setLoadingMore(false);
  };

  const hasActiveFilter = filterCampaign || filterTemplate || searchQuery;

  const handleDelete = async (s3Key) => {
    try {
      await deleteKbFile(s3Key);
      setDisplayFiles(prev => prev.filter(f => f.s3Key !== s3Key));
      setPendingDelete(null);
      setAlert?.({ variant: 'success', heading: 'Deleted', text: 'KB file deleted.' });
    } catch (err) {
      console.error('Delete failed', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to delete KB file.' });
    }
  };

  const handleAttachSave = async ({ s3Key, fileName, selectedCampaigns: sc, selectedTemplates: st, global = false }) => {
    setSavingFile(s3Key);
    try {
      await attachKbBatch({ fileName, s3Key, campaigns: sc, templates: st, replace: true, global });
      setAttachingFile(null);
      setExpandedFile(null);
      await loadFiles({ reset: true });
      setAlert?.({ variant: 'success', heading: 'Updated', text: 'Attachments updated.' });
    } catch (err) {
      console.error('Attach failed', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to update attachments.' });
    } finally {
      setSavingFile(null);
    }
  };

  const toggleExpand = (s3Key) => {
    setExpandedFile(prev => prev === s3Key ? null : s3Key);
    setAttachingFile(null);
  };

  const toggleAttach = (file) => {
    setAttachingFile(prev => prev?.s3Key === file.s3Key ? null : file);
    setExpandedFile(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    setUploadFiles(prev => {
      const existing = new Set(prev.map(f => f.name));
      return [...prev, ...dropped.filter(f => !existing.has(f.name))];
    });
  };

  const handleFileInput = (e) => {
    const picked = Array.from(e.target.files);
    setUploadFiles(prev => {
      const existing = new Set(prev.map(f => f.name));
      return [...prev, ...picked.filter(f => !existing.has(f.name))];
    });
    e.target.value = '';
  };

  const removeUploadFile = (name) => setUploadFiles(prev => prev.filter(f => f.name !== name));

  const uploadScorecardOptions = () => {
    const groups = campaigns
      .filter(c => (campaignMap[c.campaignName] || []).length > 0)
      .map(c => ({
        group: c.campaignName,
        items: (campaignMap[c.campaignName] || []).map(t => ({ value: t, label: t }))
      }));

    const unassigned = templates
      .map(t => t.templateName)
      .filter(t => !templateCampaignMap[t]?.length);

    if (unassigned.length) {
      groups.push({ group: 'Unassigned', items: unassigned.map(t => ({ value: t, label: t })) });
    }

    return groups;
  };

  const handleUpload = async () => {
    if (!uploadFiles.length) {
      setAlert?.({ variant: 'warning', heading: 'Missing info', text: 'Select at least one file.' });
      return;
    }
    if (!isUploadGlobal) {
      const targets = attachTo === 'campaign' ? selectedCampaigns : selectedTemplates;
      if (targets.length === 0) {
        setAlert?.({
          variant: 'warning',
          heading: 'Missing info',
          text: `Select at least one ${attachTo === 'campaign' ? 'queue' : 'scorecard'}, or enable global.`
        });
        return;
      }
    }
    setUploading(true);
    try {
      for (const file of uploadFiles) {
        const targets = attachTo === 'campaign' ? selectedCampaigns : selectedTemplates;
        const q_id = !isUploadGlobal && attachTo === 'campaign' ? targets[0] : undefined;
        const template_id = !isUploadGlobal && attachTo === 'template' ? targets[0] : undefined;
        const s3Up = await presign(file.name, 'kb', q_id, template_id);
        await axios.put(s3Up.url, file);
        await attachKbBatch({
          fileName: file.name,
          s3Key: s3Up.key,
          campaigns: isUploadGlobal || attachTo !== 'campaign' ? [] : selectedCampaigns,
          templates: isUploadGlobal || attachTo !== 'template' ? [] : selectedTemplates,
          global: isUploadGlobal
        });
      }
      setUploadFiles([]);
      setSelectedCampaigns([]);
      setSelectedTemplates([]);
      setIsUploadGlobal(false);
      setAlert?.({ variant: 'success', heading: 'Uploaded', text: 'KB file(s) uploaded and attached.' });
      await loadFiles({ reset: true });
    } catch (err) {
      console.error('Upload failed', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to upload KB file.' });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: 80, color: '#9ca3af', fontSize: 13 }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1a2b3c', margin: 0 }}>Knowledge base</h1>

      {/* ------------------------------------------------------------------ */}
      {/* Filter bar                                                           */}
      {/* ------------------------------------------------------------------ */}
      <div className="kb-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 2, minWidth: 180 }}>
            <label className="kb-lbl">Search</label>
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              className="kb-input"
            />
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <label className="kb-lbl">By Queue</label>
            <select value={filterCampaign} onChange={e => handleFilterCampaign(e.target.value)} className="kb-select">
              <option value="">All queues</option>
              {campaigns.map(c => <option key={c.campaignName} value={c.campaignName}>{c.campaignName}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <label className="kb-lbl">By Scorecard</label>
            <select value={filterTemplate} onChange={e => handleFilterTemplate(e.target.value)} className="kb-select">
              <option value="">All scorecards</option>
              {templates.map(t => <option key={t.templateName} value={t.templateName}>{t.templateName}</option>)}
            </select>
          </div>
          {hasActiveFilter && (
            <button className="kb-btn" style={{ alignSelf: 'flex-end' }} onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>

        {hasActiveFilter && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {filterCampaign && (
              <span className="kb-fp kb-fp-q">
                Queue: {filterCampaign}
                <button onClick={() => handleFilterCampaign('')}>✕</button>
              </span>
            )}
            {filterTemplate && (
              <span className="kb-fp kb-fp-s">
                Scorecard: {filterTemplate}
                <button onClick={() => handleFilterTemplate('')}>✕</button>
              </span>
            )}
            {searchQuery && (
              <span className="kb-fp kb-fp-none">
                Search: {searchQuery}
                <button onClick={() => handleSearch('')}>✕</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* File list                                                            */}
      {/* ------------------------------------------------------------------ */}
      <div className="kb-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 className="kb-section-title">KB files</h2>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>
            {hasMore ? `${displayFiles.length}+ files` : `${displayFiles.length} file${displayFiles.length !== 1 ? 's' : ''}`}
          </span>
        </div>

        {displayFiles.length === 0 ? (
          <p style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: '20px 0' }}>
            {hasActiveFilter ? 'No files match your filters.' : 'No KB files found.'}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {displayFiles.map(file => {
              const isExpanded = expandedFile === file.s3Key;
              const isAttaching = attachingFile?.s3Key === file.s3Key;
              const isPanelOpen = isExpanded || isAttaching;
              const iconClass = fileIconClass(file.fileName);
              const iconLabel = fileIconLabel(file.fileName);

              return (
                <div key={file.s3Key}>
                  {/* File row */}
                  <div className={`kb-file-row${isPanelOpen ? ' open' : ''}`}>
                    {/* File type icon */}
                    <div className={`kb-file-icon ${iconClass}`}>{iconLabel}</div>

                    {/* File info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="kb-file-name">{file.fileName}</p>
                      <AttachmentSummary
                        file={file}
                        onExpand={() => toggleExpand(file.s3Key)}
                      />
                      {file.uploadedAt && (
                        <p className="kb-file-date">
                          {new Date(file.uploadedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => !savingFile && toggleAttach(file)}
                        disabled={savingFile === file.s3Key}
                        className={`kb-btn-attach${isAttaching ? ' on' : ''}`}
                      >
                        {savingFile === file.s3Key ? 'Saving...' : isAttaching ? 'Close' : 'Attach'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDelete(pendingDelete === file.s3Key ? null : file.s3Key)}
                        className="kb-btn-icon"
                        title="Delete file"
                      >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  </div>

                  {/* Detail expand panel — read only, triggered by "+N more" pill */}
                  {isExpanded && (
                    <AttachmentDetail
                      file={file}
                      campaignMap={campaignMap}
                      onClose={() => setExpandedFile(null)}
                      onEdit={() => { setExpandedFile(null); setAttachingFile(file); }}
                    />
                  )}

                  {/* Attach / edit panel */}
                  {isAttaching && (
                    <AttachPanel
                      file={file}
                      campaigns={campaigns}
                      templates={templates}
                      campaignMap={campaignMap}
                      templateCampaignMap={templateCampaignMap}
                      onSave={handleAttachSave}
                      onCancel={() => setAttachingFile(null)}
                      saving={savingFile === file.s3Key}
                    />
                  )}

                  {/* Inline delete confirmation */}
                  {pendingDelete === file.s3Key && (
                    <div className="kb-del-bar">
                      <span>
                        Delete <strong>{file.fileName}</strong>? This cannot be undone.
                      </span>
                      <div className="kb-del-actions">
                        <button
                          className="kb-btn kb-btn-danger"
                          onClick={() => handleDelete(file.s3Key)}
                        >
                          Yes, delete
                        </button>
                        <button
                          className="kb-btn"
                          onClick={() => setPendingDelete(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {hasMore && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12 }}>
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="kb-btn"
            >
              {loadingMore ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Upload & attach                                                      */}
      {/* ------------------------------------------------------------------ */}
      <div className="kb-card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <h2 className="kb-section-title">Upload & attach</h2>

        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`kb-drop-zone${dragging ? ' dragging' : ''}`}
        >
          <svg style={{ width: 28, height: 28, color: '#B5D4F4', margin: '0 auto 8px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M12 3v12M8 7l4-4 4 4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
          </svg>
          <p style={{ fontSize: 13, color: '#374151' }}>
            Drop files here or <span style={{ color: '#185FA5', fontWeight: 600 }}>browse</span>
          </p>
          <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>PDF, DOCX, TXT — up to 25 MB each</p>
          <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileInput} />
        </div>

        {/* File chips */}
        {uploadFiles.length > 0 && (
          <div className="kb-chips">
            {uploadFiles.map(f => (
              <span key={f.name} className="kb-chip kb-chip-file">
                {f.name}
                <button type="button" className="kb-chip-x" onClick={() => removeUploadFile(f.name)}>✕</button>
              </span>
            ))}
          </div>
        )}

        {/* Global toggle */}
        <label className={`kb-global-toggle${isUploadGlobal ? ' active' : ''}`}>
          <input
            type="checkbox"
            checked={isUploadGlobal}
            onChange={e => { setIsUploadGlobal(e.target.checked); setSelectedCampaigns([]); setSelectedTemplates([]); }}
          />
          <div className="kb-global-toggle-text">
            <div className="kb-global-toggle-title">Apply to all queues (global)</div>
            <div className="kb-global-toggle-sub">High-level overview — overrides queue &amp; scorecard selection</div>
          </div>
          {isUploadGlobal && (
            <span className="kb-pill kb-pill-global">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 3 }}>
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
              </svg>
              Global
            </span>
          )}
        </label>

        {/* Attach-to toggle + target selector — dims when global */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start', ...(isUploadGlobal ? { opacity: 0.35, pointerEvents: 'none' } : {}) }}>
            <div>
              <label className="kb-lbl">Attach to</label>
              <div className="kb-tab-wrap">
                {['campaign', 'template'].map(mode => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => { setAttachTo(mode); setSelectedCampaigns([]); setSelectedTemplates([]); }}
                    className={`kb-tab${attachTo === mode ? ' active' : ''}`}
                  >
                    {mode === 'campaign' ? 'Queue' : 'Scorecard'}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 200 }}>
              <label className="kb-lbl">
                {attachTo === 'campaign' ? 'Queues' : 'Scorecards'}
              </label>
              {attachTo === 'campaign' ? (
                <MultiSelect
                  options={campaigns.map(c => c.campaignName)}
                  selected={selectedCampaigns}
                  onChange={setSelectedCampaigns}
                  placeholder="Select queues..."
                  chipClass="kb-chip-q"
                />
              ) : (
                <MultiSelect
                  options={uploadScorecardOptions()}
                  selected={selectedTemplates}
                  onChange={setSelectedTemplates}
                  placeholder="Select scorecards..."
                  chipClass="kb-chip-s"
                />
              )}
            </div>
          </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="kb-btn kb-btn-primary"
          >
            {uploading ? 'Uploading...' : 'Upload & Attach'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KbManager;